import type { Ref } from 'vue'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

interface NetworkInformationLike {
    saveData?: boolean
}

const prefersStillImage = (): boolean => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection
    return Boolean(window.matchMedia?.(REDUCED_MOTION_QUERY).matches || connection?.saveData)
}

/**
 * Loads a looping background video after the page has loaded, so it never competes
 * with the hero image or text for bandwidth. The hero image stays visible until then,
 * and for visitors who ask for reduced motion or data saving.
 * WCAG 2.2.2: `toggle` pauses and resumes the motion.
 */
export const useBackgroundVideo = (video: Ref<HTMLVideoElement | null>, src: Ref<string>) => {
    const isPaused = ref(false)

    const play = async () => {
        const element = video.value
        if (!element || !src.value) return
        if (!element.src) element.src = src.value
        await element.play()
        isPaused.value = false
    }

    const pause = () => {
        video.value?.pause()
        isPaused.value = true
    }

    const toggle = () => (isPaused.value ? play() : pause())

    const startWhenLoaded = () => {
        if (document.readyState === 'complete') {
            play()
            return
        }
        window.addEventListener('load', () => play(), { once: true })
    }

    onMounted(() => {
        if (prefersStillImage()) {
            isPaused.value = true
            return
        }
        startWhenLoaded()
    })

    return { isPaused, toggle }
}
